import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { useTheme } from '../../hooks/useTheme';
import { TooltipProps } from './types';
import { getStylesByPosition } from './Tooltip.helpers';
import Portal from '../Portal';
import { useStyles } from './Tooltip.styles';
import { flattenClassNames } from '@trukt/portal-utils/flattenClassNames';

export type { TooltipProps };

export const Tooltip = (props: TooltipProps) => {
  const {
    children,
    title = '',
    color,
    position = 'top',
    delay = 0,
    styles = {},
    className,
  } = props;
  const theme = useTheme();
  const basicStyles = useStyles(props);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [doesRemoveOpacity, setDoesRemoveOpacity] = useState<boolean>(false);
  const [positionRef, setPositionRef] = useState<HTMLSpanElement | null>(null);
  const [tooltipRef, setTooltipRef] = useState<HTMLDivElement | null>(null);

  const positionStyles = getStylesByPosition({
    color: color ?? theme.palette.background.primary,
    positionRef,
    tooltipRef,
  })[position];

  const closeTooltip = () => {
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    setDoesRemoveOpacity(false);
  };

  const openTooltip = () => {
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setDoesRemoveOpacity(true);
    }, delay);
  };

  useEffect(
    () => () => {
      if (timeoutRef.current !== undefined) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  return (
    <>
      <span
        className={flattenClassNames(['tooltip-container', className])}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        ref={(ref) => {
          setPositionRef(ref);
        }}
      >
        {children}
      </span>
      {doesRemoveOpacity && title && (
        <Portal key={title.toString()}>
          <div
            ref={(ref) => {
              setTooltipRef(ref);
            }}
            css={css([basicStyles.tooltipContainer, positionStyles, styles])}
          >
            {title}
          </div>
        </Portal>
      )}
    </>
  );
};
